import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { imgUrl } from "../../lib/api";

/**
 * Fullscreen image lightbox — used by ProductDetail (main image + all
 * gallery images) and Home / Shop category tiles (single image).
 *
 * Design contract:
 *   - Opens over any surface (z-[100]) with a dark backdrop.
 *   - Respects iOS safe-area insets so the close button never hides
 *     behind the Dynamic Island.
 *   - Native pinch-to-zoom via `touch-action: pinch-zoom` on the
 *     scroll container (WebKit allows this even when the app-level
 *     meta viewport disables page zoom, because it's scoped to the
 *     element).
 *   - Desktop: click-to-toggle a 2× zoom on the image (uses transform
 *     so we don't force the browser to re-decode a huge asset).
 *   - Keyboard: ESC closes, ←/→ navigate between images.
 *   - Touch: swipe left/right navigates. Vertical drag beyond 80px
 *     closes (matches native photo viewer UX).
 *   - Locks body scroll while open.
 *
 * Purely additive — mounting this component never mutates the caller's
 * image data or handlers; callers pass `images`, `initialIndex`,
 * `open`, and an `onClose` callback.
 */
export function ImageLightbox({
  open,
  images,
  initialIndex = 0,
  onClose,
  alt = "",
}) {
  const [index, setIndex] = useState(initialIndex);
  const [zoomed, setZoomed] = useState(false);
  const touchStart = useRef(null);
  const total = Array.isArray(images) ? images.length : 0;

  const prev = useCallback(
    () => setIndex((i) => (i - 1 + total) % total),
    [total]
  );
  const next = useCallback(
    () => setIndex((i) => (i + 1) % total),
    [total]
  );

  // Reset internal state each time the lightbox re-opens.
  useEffect(() => {
    if (open) {
      setIndex(initialIndex);
      setZoomed(false);
    }
  }, [open, initialIndex]);

  // ESC / arrow-key handling + body-scroll lock.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, prev, next]);

  if (!open || total === 0) return null;

  const src = imgUrl(images[index]);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    // Vertical drag down → dismiss (native photo-viewer gesture)
    if (Math.abs(dy) > 80 && Math.abs(dy) > Math.abs(dx)) {
      onClose?.();
    } else if (Math.abs(dx) > 40 && total > 1) {
      dx < 0 ? next() : prev();
    }
    touchStart.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 select-none"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt || "Image viewer"}
      data-testid="image-lightbox"
    >
      {/* Close button — safe-area aware so it never hides under the notch. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        className="absolute right-4 z-10 bg-black/60 hover:bg-black/80 rounded-full w-11 h-11 flex items-center justify-center text-white transition-colors"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 1rem)" }}
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X size={22} />
      </button>

      {/* Image surface — pinch-zoom enabled on this element only. */}
      <div
        className="w-full h-full overflow-auto flex items-center justify-center px-2"
        style={{ touchAction: "pinch-zoom" }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="lightbox-scroll"
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          onClick={(e) => {
            // Desktop convenience: click toggles a 2× zoom via transform,
            // giving mouse users an equivalent to mobile pinch-zoom.
            e.stopPropagation();
            setZoomed((z) => !z);
          }}
          className="max-w-full max-h-full object-contain cursor-zoom-in transition-transform duration-200"
          style={{
            transform: zoomed ? "scale(2)" : "scale(1)",
            cursor: zoomed ? "zoom-out" : "zoom-in",
          }}
          data-testid="lightbox-image"
        />
      </div>

      {/* Multi-image controls: prev / next arrows + counter pill. */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 rounded-full w-11 h-11 items-center justify-center text-white transition-colors"
            aria-label="Previous image"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 rounded-full w-11 h-11 items-center justify-center text-white transition-colors"
            aria-label="Next image"
            data-testid="lightbox-next"
          >
            <ChevronRight size={22} />
          </button>
          <div
            className="absolute left-1/2 -translate-x-1/2 text-white text-sm bg-black/60 rounded-full px-3 py-1"
            style={{
              bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)",
            }}
            data-testid="lightbox-counter"
          >
            {index + 1} / {total}
          </div>
        </>
      )}
    </div>
  );
}

export default ImageLightbox;
